import type { ISchedulerDependency, ISchedulerTask, LinkType } from '@interfaces/IScheduler';
import { addDays } from './timeScale';
import { hasChildren } from './commitReadiness';
import { isScheduled } from './taskDates';

// ---------------------------------------------------------------------------
// splitTaskByLocations — turn one task into a leaf subtask PER location.
//
// Pure / side-effect free: the Split modal uses it for both the live preview
// and the dispatched payload, and the reducer just applies the precomputed
// arrays atomically (one undo entry). Floor/zone domain knowledge stays in the
// modal, which hands this helper a flat, already-ordered list of rows.
// ---------------------------------------------------------------------------

/**
 * Whether the drawer offers Split by location. Splitting turns a leaf into a
 * summary, across the areas it already covers — so only a non-milestone leaf
 * with ≥ 2 areas whose scope isn't frozen upstream. And only a SCHEDULED one:
 * the subtasks are laid out from the parent's start, which an undated
 * (imported) task doesn't have (QA F-37).
 */
export function canSplitByLocations(
  task: ISchedulerTask,
  tasks: readonly ISchedulerTask[],
  frozen: boolean,
): boolean {
  return (
    !task.isMilestone &&
    isScheduled(task) &&
    !hasChildren(tasks, task.id) &&
    (task.locations?.length ?? 0) >= 2 &&
    !frozen
  );
}

/** How the generated subtasks relate to each other. */
export type SplitMode = 'dependencies' | 'parallel';

/** The link applied between EVERY consecutive pair of subtasks (dependencies mode). */
export interface ISplitLink {
  type: LinkType;
  lagDays: number;
}

export const DEFAULT_SPLIT_LINK: ISplitLink = { type: 'FS', lagDays: 0 };

export interface ISplitRow {
  /** Stable key for the row (floor id or zone id) — drives deterministic ids. */
  key: string;
  /** Suffix appended to the parent's name, e.g. '4F' or '4F · A'. */
  label: string;
  /**
   * Zone ids the subtask occupies: one entry in area mode, all of a floor's
   * assigned zones in floor mode.
   */
  locationIds: string[];
  /** Inclusive duration in days; clamped to ≥ 1. */
  durationDays: number;
}

export interface ILocationSplit {
  subtasks: ISchedulerTask[];
  dependencies: ISchedulerDependency[];
}

/**
 * Earliest start the link allows, mirroring the cascade's link semantics —
 * notably FS lag 0 is SAME-DAY (successor starts ON the predecessor's finish
 * day, the Genba chaining convention). Subtasks are placed exactly AT their
 * constraint so the configured link/gap is also the visible geometry, and the
 * chain stays pinned for the cascade's pull-on-lag-decrease behaviour.
 */
function constrainedStart(
  predStart: string,
  predEnd: string,
  link: ISplitLink,
  durationInclusive: number,
): string {
  switch (link.type) {
    case 'FS':
      return addDays(predEnd, link.lagDays);
    case 'SS':
      return addDays(predStart, link.lagDays);
    case 'FF':
      return addDays(addDays(predEnd, link.lagDays), -(durationInclusive - 1));
    case 'SF':
      return addDays(addDays(predStart, link.lagDays), -(durationInclusive - 1));
  }
}

/**
 * Build the subtasks (and, in dependencies mode, the links chaining them) for
 * a location split. Dates are inclusive on both ends, mirroring `ISchedulerTask`.
 *
 * - dependencies → consecutive subtasks are linked with ONE shared link type +
 *   gap (default FS / 0 = starts the day the previous ends) and dated exactly
 *   at that constraint.
 * - parallel     → every subtask starts at the parent's start, no links.
 *
 * Ids are derived from the parent id + index (no `Date.now()`), so the result
 * is deterministic and collision-free within the parent's subtree.
 */
export function buildLocationSplit(args: {
  task: ISchedulerTask;
  rows: ISplitRow[];
  mode: SplitMode;
  /** Link between consecutive subtasks; dependencies mode only. */
  link?: ISplitLink;
}): ILocationSplit {
  const { task, rows, mode, link = DEFAULT_SPLIT_LINK } = args;
  const subtasks: ISchedulerTask[] = [];
  const dependencies: ISchedulerDependency[] = [];

  rows.forEach((row, i) => {
    const duration = Math.max(1, Math.floor(row.durationDays));
    const prev = subtasks[i - 1];
    const start =
      mode === 'dependencies' && prev
        ? constrainedStart(prev.start, prev.end, link, duration)
        : task.start;
    const end = addDays(start, duration - 1);
    subtasks.push({
      id: `${task.id}-loc-${i + 1}`,
      groupId: task.groupId,
      parentId: task.id,
      name: `${task.name} – ${row.label}`,
      start,
      end,
      isMilestone: false,
      // A split subtask is a fresh PLAN row: it inherits the areas it covers,
      // but never its parent's commit — each is promised on its own.
      status: 'planned',
      kind: task.kind,
      committed: false,
      progressPercent: 0,
      ...(task.color ? { color: task.color } : {}),
      ...(task.profession ? { profession: task.profession } : {}),
      locations: row.locationIds.map((locationId) => ({ locationId, status: 'unplanned' as const })),
    });
    if (mode === 'dependencies' && prev) {
      dependencies.push({
        id: `${task.id}-loc-dep-${i}`,
        predecessorId: prev.id,
        successorId: subtasks[i]!.id,
        type: link.type,
        lagDays: link.lagDays,
      });
    }
  });

  return { subtasks, dependencies };
}
